import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';
import {
  getInstallOnPhoneLearnPath,
  INSTALL_APP_DISMISS_KEY,
  isPwaStandalone,
} from '../../utils/pwaInstall';

/**
 * Soft reminder for logged-in mobile users who are still in the browser
 * (not installed to the home screen). Keeps copy simple — no "PWA" jargon.
 */
export default function InstallAppBanner() {
  const { user } = useContext(AuthContext);
  const isMobile = useIsMobile();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user?.token || !isMobile) {
      setVisible(false);
      return;
    }
    if (isPwaStandalone()) {
      setVisible(false);
      return;
    }
    if (location.pathname.startsWith('/onboarding')) {
      setVisible(false);
      return;
    }
    try {
      if (localStorage.getItem(INSTALL_APP_DISMISS_KEY)) {
        setVisible(false);
        return;
      }
    } catch {
      // ignore
    }
    setVisible(true);
  }, [user?.token, isMobile, location.pathname]);

  const dismiss = () => {
    try {
      localStorage.setItem(INSTALL_APP_DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  const guidePath = getInstallOnPhoneLearnPath();

  return (
    <div
      role="region"
      aria-label="Add to home screen"
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: 'rgba(91, 108, 255, 0.12)',
        borderBottom: '1px solid rgba(91, 108, 255, 0.28)',
        color: '#e8edf4',
        fontSize: '0.875rem',
        lineHeight: 1.35,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>Add PrecisionTrader to your home screen</span>
        <span style={{ color: 'rgba(232, 237, 244, 0.72)' }}>
          {' '}
          — opens like an app.{' '}
        </span>
        <Link
          to={guidePath}
          style={{ color: '#8b9bff', fontWeight: 600, textDecoration: 'underline' }}
        >
          How to add it
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 999,
          color: 'rgba(232, 237, 244, 0.75)',
          padding: '6px 12px',
          fontSize: '0.8rem',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Not now
      </button>
    </div>
  );
}
